import * as React from 'react';
import {View} from 'react-native';
import {get} from 'lodash';

import KeyboardFocusable from '/component/base/ExtKeyboard/KeyboardFocusable';
import Icon from '/component/base/Icon';
import Text from '/component/base/Text';
import Gutter from '/component/layout/Gutter';
import {BonusesID, CoachInfoCard} from '/constant/TestIDs';
import {useTranslation} from '/provider/TranslationProvider';
import {getDateUtilDefaultInstance} from '/util/date.util';

import {
  BONUS_TIERS,
  ELIGIBLE_ACCELERATOR_FALLBACK_TITLE,
  ELIGIBLE_THROUGH_FALLBACK,
  NEW_CLIENTS_PQV_FALLBACK,
  SLOTS_PER_BONUS,
  TOTAL_BONUSES,
} from './constants';
import {styles} from './EligibleAcceleratorBonusReportContent.style';
import {EligibleAcceleratorBonusReportContentProps} from './types';
import {
  getCappedOrderingEntities,
  getCompletedBonuses,
  getCurrentDate,
  getDaysLeft,
  getEligibleThroughDate,
  getFilledInTier,
  getGlobalProgressPercent,
  getTierSlotNumbers,
  parseDateSafe,
} from './util';

export const EligibleAcceleratorBonusReportContent: React.FC<
  EligibleAcceleratorBonusReportContentProps
> = ({recordData = {}}) => {
  const dateUtil = getDateUtilDefaultInstance();
  const newOrderingEntities = get(recordData, 'cabEligibilityOECount.total', 0);
  const cappedOrderingEntities = getCappedOrderingEntities(newOrderingEntities);
  const currentDate = getCurrentDate(recordData);
  const eligibleAccelThrough = getEligibleThroughDate(recordData);
  const currentDateObj = parseDateSafe(currentDate);
  const eligibleThroughObj = parseDateSafe(eligibleAccelThrough);
  const completedBonuses = getCompletedBonuses(cappedOrderingEntities);
  const globalProgressPercent = getGlobalProgressPercent(completedBonuses);
  const daysLeft = getDaysLeft(currentDateObj, eligibleThroughObj);

  const {tt} = useTranslation('reportCardContent.eligibleAccelerator');
  const headerTitle =
    tt('title', ELIGIBLE_ACCELERATOR_FALLBACK_TITLE) ||
    ELIGIBLE_ACCELERATOR_FALLBACK_TITLE;

  const accessibilityLabel =
    completedBonuses === 0
      ? `Bonus progress: ${completedBonuses} out of ${TOTAL_BONUSES} bonuses`
      : `Bonus progress: ${completedBonuses} out of ${TOTAL_BONUSES} bonuses completed`;

  const accessibilityValue =
    completedBonuses === 0
      ? undefined
      : {
          min: 0,
          max: TOTAL_BONUSES,
          now: completedBonuses,
        };

  return (
    <View style={styles.base}>
      <View style={styles.header} accessible={false}>
        <Gutter accessible={true}>
          <Text
            variant="heading"
            style={styles.titleText}
            testID={CoachInfoCard.eligibleForABTitle}>
            {headerTitle}
          </Text>
        </Gutter>
      </View>
      <Gutter>
        <View style={styles.content} accessible={false}>
          <View
            style={styles.globalProgressRow}
            accessible={true}
            accessibilityValue={accessibilityValue}
            accessibilityLabel={accessibilityLabel}>
            <View
              style={styles.globalProgressTrack}
              testID={BonusesID.bonusesProgress}>
              <View
                style={[
                  styles.globalProgressFill,
                  {width: `${globalProgressPercent}%`},
                ]}
              />
            </View>
            <Text
              accessible={false}
              variant="body"
              style={styles.globalProgressLabel}
              testID={BonusesID.bonusesProgressLabel}>
              {completedBonuses}/{TOTAL_BONUSES} bonuses
            </Text>
          </View>

          <View style={styles.periodHeaderRow}>
            <View
              accessible={true}
              accessibilityLabel={dateUtil.formatDate(
                currentDate,
                'MMMM yyyy',
              )}>
              <Text
                variant="heading"
                style={styles.monthText}
                testID={BonusesID.periodMonthLabel}>
                {dateUtil.formatDate(currentDate, 'MMMM yyyy') || '--'}
              </Text>
            </View>
            <View
              accessible={true}
              accessibilityLabel={`${daysLeft} days left`}>
              <Text
                variant="heading"
                style={styles.daysLeftText}
                testID={BonusesID.daysLeftLabel}>
                {daysLeft} days left
              </Text>
            </View>
          </View>

          <View accessible={true}>
            <Text
              variant="caption"
              style={styles.pqvHeaderText}
              testID={BonusesID.newClientsPQVHeader}>
              {tt('newClientsWith', NEW_CLIENTS_PQV_FALLBACK)}
            </Text>
          </View>

          {BONUS_TIERS.map(({bonusNumber, amountLabel, startSlot}) => {
            const filledInTier = getFilledInTier(
              cappedOrderingEntities,
              startSlot,
            );
            const isComplete = filledInTier === SLOTS_PER_BONUS;
            const slotNumbers = getTierSlotNumbers(startSlot);

            return (
              <View
                key={bonusNumber}
                style={styles.tierBlock}
                accessible={false}
                testID={BonusesID.bonusTier}>
                <View
                  style={styles.tierHeaderRow}
                  testID={BonusesID.bonusTierHeader}
                  accessible={true}
                  accessibilityLabel={`Bonus ${bonusNumber}, reward ${amountLabel}, ${
                    isComplete
                      ? 'completed'
                      : `${filledInTier} of ${SLOTS_PER_BONUS} slots filled`
                  }`}>
                  <Text
                    variant="button"
                    style={styles.bonusTitleText}
                    testID={BonusesID.bonusTitle}>
                    {`Bonus ${bonusNumber}`}
                  </Text>
                  <Text
                    variant="heading"
                    style={styles.bonusAmountText}
                    testID={BonusesID.bonusAmount}>
                    {amountLabel}
                  </Text>
                  {isComplete && (
                    <View
                      style={styles.completeContainer}
                      testID={BonusesID.bonusCompleteBadge}>
                      <Icon name="check" style={styles.completeIcon} />
                      <Text variant="caption" style={styles.completeText}>
                        Complete
                      </Text>
                    </View>
                  )}
                </View>

                <View
                  style={styles.slotsRow}
                  testID={BonusesID.bonusSlotsRow}
                  accessible={false}>
                  {slotNumbers.map(slotNumber => {
                    const isFilled = slotNumber <= cappedOrderingEntities;
                    return (
                      <View
                        key={slotNumber}
                        style={[styles.slot, isFilled && styles.slotFilled]}>
                        <KeyboardFocusable
                          style={styles.slotInner}
                          testID={`bonus-slot-${slotNumber}`}
                          accessibilityLabel={`Bonus ${bonusNumber}, Client ${slotNumber}`}
                          accessibilityRole="checkbox"
                          accessibilityState={{checked: isFilled}}>
                          {isFilled ? (
                            <View
                              style={styles.filledSlotContent}
                              accessible={false}>
                              <Text
                                variant="caption"
                                accessible={false}
                                style={styles.slotNumberFilled}>
                                {slotNumber}
                              </Text>
                              <Icon
                                name="check"
                                style={styles.slotCheckIcon}
                                accessible={false}
                              />
                            </View>
                          ) : (
                            <Text
                              variant="caption"
                              style={styles.slotNumber}
                              accessible={false}>
                              {slotNumber}
                            </Text>
                          )}
                        </KeyboardFocusable>
                      </View>
                    );
                  })}
                </View>
              </View>
            );
          })}
        </View>
      </Gutter>
      <Gutter>
        <View style={styles.footerRow} testID={CoachInfoCard.eligibleThrough}>
          <View accessible={true}>
            <Text
              accessible={false}
              variant="heading"
              style={styles.footerLabelText}
              testID={CoachInfoCard.eligibleThroughLabel}>
              {tt('eligible through', ELIGIBLE_THROUGH_FALLBACK)}
            </Text>
          </View>

          <View accessible={true}>
            <Text
              accessible={false}
              variant="button"
              style={styles.footerValueText}
              testID={CoachInfoCard.eligibleThroughValue}>
              {dateUtil.formatDate(eligibleAccelThrough, 'MMMM d, yyyy')}
            </Text>
          </View>
        </View>
      </Gutter>
    </View>
  );
};
